import * as Sentry from '@sentry/react'
// eslint-disable-next-line no-restricted-imports
import { Modal } from 'antd'
import { isAxiosError } from 'axios'
import { createElement } from 'react'
import toast from 'react-hot-toast'

import {
  ACTIONS,
  COMMON_HTTP_ERROR_MESSAGE
} from '@/constants/common/message.constants'
import { type EnvelopedEntity } from '@/libs/api'

/**
 * 指定メッセージでエラートーストを表示し、ユーザー操作を監視して遅延閉じる
 * 最低4秒間は表示し、その後マウス操作があったタイミングで閉じる
 */
const showPersistentErrorToast = (title: string, detailMessage?: string) => {
  const content = detailMessage
    ? createElement(
        'div',
        { className: 'text-left' },
        createElement('div', { className: 'font-bold' }, title),
        createElement(
          'div',
          { className: 'font-normal whitespace-pre-wrap mt-1' },
          detailMessage
        )
      )
    : createElement(
        'div',
        { className: 'text-left font-bold whitespace-pre-wrap' },
        title
      )

  const toastId = toast.error(content, {
    duration: Infinity,
    className: 'api-error-toast'
  })

  const handleKeyDown = (e: KeyboardEvent) => {
    // Escapeキーで閉じる
    if (e.key === 'Escape') {
      dismiss()
    }
  }

  let lastDistance: number | null = null

  const handleMouseMove = (e: MouseEvent) => {
    // トースト上での操作は無視（コピーなどのため）
    if ((e.target as HTMLElement).closest('.api-error-toast')) {
      return
    }

    // 画面下部中央（トースト位置）への距離を計算
    const toastX = window.innerWidth / 2
    const toastY = window.innerHeight
    const currentDistance = Math.sqrt(
      Math.pow(e.clientX - toastX, 2) + Math.pow(e.clientY - toastY, 2)
    )

    // 初回移動時、またはトーストに近づいている（距離が縮まっている）場合は消さない
    if (lastDistance === null || currentDistance <= lastDistance) {
      lastDistance = currentDistance
      return
    }

    // トーストから遠ざかっている場合は閉じる
    dismiss()
  }

  const handleTouchStart = (e: TouchEvent) => {
    if ((e.target as HTMLElement).closest('.api-error-toast')) {
      return
    }
    dismiss()
  }

  const dismiss = () => {
    toast.dismiss(toastId)

    // イベントリスナーを削除（一度検知したら監視終了）
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('touchstart', handleTouchStart)
    window.removeEventListener('keydown', handleKeyDown)
  }

  // 4秒後にイベントリスナーを登録
  setTimeout(() => {
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('touchstart', handleTouchStart)
    window.addEventListener('keydown', handleKeyDown)
  }, 4000)
}

const showErrorToastWithDetail = (
  message: string | null | undefined,
  defaultMessage: string
) => {
  if (message) {
    showPersistentErrorToast(message, defaultMessage)
  } else {
    showPersistentErrorToast(defaultMessage)
  }
}

/**
 * 指定したエラーまたはカスタムメッセージに基づいてエラートーストメッセージを表示する
 * - エラーがAxiosError かつ レスポンスデータにメッセージが含まれている場合、そのメッセージを表示
 * - そうでない場合、カスタムメッセージが提供されていればそれを表示
 * - どちらもない場合は、トーストメッセージは表示しない
 *
 * @param error - Axiosエラーのレスポンスを含む可能性のあるエラーオブジェクト。
 * @param message - エラーにメッセージが含まれていない場合に表示するオプションのカスタムメッセージ。
 */
export const toastApiErrorMessage = (
  error: unknown,
  customMessage?: string
) => {
  const message =
    isAxiosError<EnvelopedEntity>(error) &&
    error.response?.data.message &&
    error.response.data.message.length > 0
      ? error.response.data.message
      : null

  // Sentryに通知
  // 400(Bad Request) は error、それ以外の 4xx は warning として送信
  // （Axios 経由のHTTPエラーはインターセプターでも送信されるため、ここでの warning は
  //  http ステータス情報が無い場合 beforeSend で抑制され、二重送信のノイズを低減します）
  const level: Sentry.SeverityLevel | undefined = (() => {
    if (isAxiosError(error)) {
      const status = error.response?.status
      if (typeof status === 'number') {
        if (status === 400) return 'error'
        if (status >= 401 && status < 500) return 'warning'
        if (status >= 500) return 'error'
      }
    }
    return undefined
  })()

  const eventId = Sentry.captureException(error, {
    level,
    extra: {
      message: message || customMessage
    }
  })

  const isError = isAxiosError(error)

  if (isError && !error.response) {
    showErrorToastWithDetail(
      customMessage || message, // カスタムメッセージを優先(サーバ側は汎用メッセージになるため)
      COMMON_HTTP_ERROR_MESSAGE.NETWORK_ERROR
    )
  } else if (
    isError &&
    (error.response?.status === 408 || error.response?.status === 504)
  ) {
    showErrorToastWithDetail(
      customMessage || message, // カスタムメッセージを優先(サーバ側は汎用メッセージになるため)
      COMMON_HTTP_ERROR_MESSAGE.TIMEOUT_ERROR
    )
  } else if (isError && error.response?.status === 500) {
    // 500 エラーはモーダル表示
    Modal.error({
      title: customMessage || message, // カスタムメッセージを優先(サーバ側は汎用メッセージになるため)
      content: createElement(
        'div',
        { className: 'whitespace-pre-wrap' },
        COMMON_HTTP_ERROR_MESSAGE.RESPONSE_CODE_500 + eventId
      ),
      okText: ACTIONS.CLOSE
    })
  } else if (isError && error.response?.status === 503) {
    showErrorToastWithDetail(
      customMessage || message, // カスタムメッセージを優先(サーバ側は汎用メッセージになるため)
      COMMON_HTTP_ERROR_MESSAGE.RESPONSE_CODE_503
    )
  } else if (isError && error.response?.status === 409) {
    // errorCode に基づいてさらに分岐する
    const errorCode = error.response?.data.errorCode as
      | keyof typeof COMMON_HTTP_ERROR_MESSAGE.RESPONSE_CODE_409
      | undefined

    const defaultMessage =
      (errorCode && COMMON_HTTP_ERROR_MESSAGE.RESPONSE_CODE_409[errorCode]) ||
      COMMON_HTTP_ERROR_MESSAGE.RESPONSE_CODE_409.DEFAULT

    showErrorToastWithDetail(
      customMessage || message, // カスタムメッセージを優先(サーバ側は汎用メッセージになるため)
      defaultMessage
    )
  } else {
    const showMessage = message || customMessage // サーバ側のエラーメッセージを優先
    if (showMessage) {
      showPersistentErrorToast(showMessage)
    }
  }
}
